import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, EditableDBItemV3, ModuleFE_Toaster, TS_PropRenderer, TS_Table } from '@nu-art/thunderstorm-frontend/index';
import { BadImplementationException, capitalizeFirstLetter, exists, sortArray, StaticLogger } from '@nu-art/ts-common';
import { TS_Icons } from '@nu-art/ts-styles';
import { ModuleFE_PermissionAccessLevel, ModuleFE_PermissionDomain, ModuleFE_PermissionProject } from '../../_entity.js';
import { Component_BasePermissionItemEditor } from './editor-base.js';
import { DropDownCaret, Input_Number_Blur, Input_Text_Blur } from './components.js';
import { DropDown_PermissionProject } from '../../_entity/permission-project/ui-components.js';
import { Page_ItemsEditor } from '@nu-art/thunderstorm-frontend/components/Page_ItemsEditor/index';
import { TS_EditableItemControllerProto } from '@nu-art/thunderstorm-frontend/components/TS_EditableItemControllerProto/index';
class Component_EditDomain extends Component_BasePermissionItemEditor {
    static defaultProps = {
        module: ModuleFE_PermissionDomain,
        displayResolver: (item) => `${ModuleFE_PermissionProject.cache.unique(item.projectId).name}/${item.namespace}`
    };
    //######################### logic #########################
    deleteLevel = async (editable) => {
        try {
            await editable.delete();
            this.forceUpdate();
        }
        catch (err) {
            this.logError({ ...err });
            ModuleFE_Toaster.toastError(err.errorResponse.debugMessage.split('\n')[0]);
        }
    };
    updateLevel = async (editable, prop, value) => {
        if (editable.item._id) {
            try {
                await editable.updateObj({ [prop]: value });
                return this.forceUpdate();
            }
            catch (err) {
                return this.logError(err);
            }
        }
        editable.set(prop, value);
        editable.validate();
        if (editable.hasValidationError())
            return;
        await editable.save();
        this.forceUpdate();
    };
    getEmptyLevel() {
        return {
            domainId: this.state.editable?.item._id,
        };
    }
    //######################### Render #########################
    editorContent = () => {
        const editable = this.state.editable;
        return _jsxs(_Fragment, { children: [_jsx(TS_PropRenderer.Vertical, { label: 'Project', children: _jsx(DropDown_PermissionProject.editable, { editable: editable, prop: 'projectId', caret: DropDownCaret, disabled: !!editable.item._id }) }), _jsx(TS_PropRenderer.Vertical, { label: 'Namespace', children: _jsx(Input_Text_Blur, { editable: editable, prop: 'namespace' }) }), _jsx(TS_PropRenderer.Vertical, { label: 'Levels', children: this.renderLevelsTable() })] });
    };
    //######################### Render - levels #########################
    renderLevelsTable = () => {
        const domain = this.state.editable;
        if (!domain)
            return '';
        let levels = ModuleFE_PermissionAccessLevel.cache.filter(level => level.domainId === domain.item._id);
        levels = sortArray(levels, i => i.value);
        levels.push(this.getEmptyLevel());
        return _jsx(TS_Table, { header: ['name', 'value', { widthPx: 50, header: 'action' }], headerRenderer: header => header === 'action' ? '' : capitalizeFirstLetter(header), rows: levels, cellRenderer: this.levelsCellRenderer });
    };
    levelsCellRenderer = (prop, item, index) => {
        const editable = new EditableDBItemV3(item, ModuleFE_PermissionAccessLevel)
            .setAutoSave(true)
            .setDebounceTimeout(0);
        switch (prop) {
            case 'name':
                return this.renderLevelName(editable);
            case 'value':
                return this.renderLevelValue(editable);
            case 'action':
                return this.renderLevelAction(editable);
            default:
                throw new BadImplementationException(`No renderer defined for key ${prop}`);
        }
    };
    renderLevelName = (editable) => {
        return _jsx(Input_Text_Blur, { editable: editable, prop: 'name', value: editable.item.name, placeholder: 'Enter level name', onChange: value => this.updateLevel(editable, 'name', value) });
    };
    renderLevelValue = (editable) => {
        return _jsx(Input_Number_Blur
        // @ts-ignore
        , { 
            // @ts-ignore
            editable: editable, prop: 'value', value: exists(editable.item.value) ? String(editable.item.value) : undefined, placeholder: 'Enter level value', onChange: value => this.updateLevel(editable, 'value', Number(value)) });
    };
    renderLevelAction = (editable) => {
        if (!editable.item._id)
            return;
        return _jsx(Button, { variant: 'dangerous', onClick: async () => await this.deleteLevel(editable), className: 'action-button delete', children: _jsx(TS_Icons.bin.component, {}) });
    };
}
class Controller_DomainsEditor extends TS_EditableItemControllerProto {
    static defaultProps = {
        keys: ['selected'],
        module: ModuleFE_PermissionDomain,
        editor: Component_EditDomain,
        createInitialInstance: () => ({}),
        autoSave: true
    };
}
export class PermissionDomainsEditor extends Page_ItemsEditor {
    //######################### Static #########################
    static Route = {
        key: 'domain-permission-editor',
        path: 'domain-permission-editor',
        Component: this
    };
    static defaultProps = {
        keys: ['selected'],
        module: ModuleFE_PermissionDomain,
        mapper: domain => [`${ModuleFE_PermissionProject.cache.unique(domain.projectId).name}/${domain.namespace}`],
        sort: (items) => sortArray(items, domain => `${ModuleFE_PermissionProject.cache.unique(domain.projectId).name}/${domain.namespace}`),
        itemRenderer: domain => _jsx(_Fragment, { children: `${ModuleFE_PermissionProject.cache.unique(domain.projectId).name}/${domain.namespace}` }),
        EditorRenderer: Controller_DomainsEditor,
        route: this.Route,
        contextMenuActions: [
            {
                label: 'Delete Domain',
                action: async (item) => {
                    try {
                        await ModuleFE_PermissionDomain.v1.delete(item).executeSync();
                        return true;
                    }
                    catch (err) {
                        StaticLogger.logError(err);
                        ModuleFE_Toaster.toastError(err.errorResponse.debugMessage.split('\n')[0]);
                    }
                }
            }
        ]
    };
    //######################### Logic #########################
    renderHeader() {
        return _jsx(_Fragment, { children: "Permission Domain" });
    }
}
//# sourceMappingURL=permission-domains-editor.js.map